import { useState } from 'react'
import type { FormEvent } from 'react'
import {
  useApproveCashRequest,
  useCashRequests,
  useRejectCashRequest,
  useSettings,
} from '@/lib/queries'
import { PageHeader, Card, Callout, PageStack } from '@/components/ui/Page'
import { Table } from '@/components/ui/Table'
import type { Column } from '@/components/ui/Table'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { TextField } from '@/components/ui/Field'
import { useToast } from '@/components/ui/Toast'
import { ApiError } from '@/lib/api'
import { formatMoney } from '@/lib/format'
import type { CashRequest } from '@/types/api'
import styles from './CashRequestsPage.module.css'

/**
 * Students waiting at the counter to pay for a plan in cash.
 *
 * *** THIS CLIENT HAS NO PAYMENT GATEWAY AT ALL ***
 * The student picks a plan in the app and taps Buy, which creates a REQUEST
 * rather than a checkout. They then pay a person at the institute, and the
 * admin confirms it here. That confirmation is the only thing that activates
 * the plan — there is no webhook, and nothing else will ever activate it.
 *
 * *** APPROVING IS TAKING RESPONSIBILITY FOR MONEY ***
 * Which is why the confirm dialog restates the amount and records who
 * approved it. A cash payment has no gateway record behind it, so the note
 * written here IS the receipt — the only answer to "the student says they
 * paid" a month later.
 */
export function CashRequestsPage() {
  const toast = useToast()
  const settings = useSettings()

  // Only once settings have loaded. Assuming either mode while the request is
  // in flight would flash the wrong screen on every visit.
  const paymentMode = settings.data?.payment_mode
  const isCash = paymentMode === 'CASH'
  const isGateway = paymentMode === 'GATEWAY'

  /*
    *** NOT EVEN ASKED FOR ON A GATEWAY CLIENT ***
    The server refuses this queue with a 409 for a gateway tenant, because the
    rows it would return are abandoned checkouts rather than people at a
    counter. Skipping the request means a gateway admin who lands here reads an
    explanation instead of an error.
  */
  const { data, isLoading, error, refetch } = useCashRequests(isCash)
  const approveMutation = useApproveCashRequest()
  const rejectMutation = useRejectCashRequest()

  const [approving, setApproving] = useState<CashRequest | null>(null)
  const [rejecting, setRejecting] = useState<CashRequest | null>(null)

  const requests = data?.requests ?? []

  const handleApprove = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!approving) return

    const form = new FormData(event.currentTarget)
    const note = String(form.get('collection_note') ?? '').trim()
    const who = approving.student_name ?? 'The student'

    try {
      await approveMutation.mutateAsync({
        subscriptionId: approving.subscription_id,
        // Optional by design: an admin clearing twenty requests should not be
        // forced to invent twenty notes, and a forced field gets filled with
        // "ok". Empty must reach the server as null, not "".
        collectionNote: note || null,
      })
      toast.success(`${who} can now book sessions.`)
      setApproving(null)
    } catch (caught) {
      toast.error(
        caught instanceof ApiError
          ? caught.message
          : 'Could not approve this request.',
      )
    }
  }

  const handleReject = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!rejecting) return

    const form = new FormData(event.currentTarget)
    const reason = String(form.get('reason') ?? '').trim()

    // The server requires it too; caught here so the admin reads a question
    // rather than a validation failure.
    if (reason.length < 3) {
      toast.error('Please say why this request is being turned down.')
      return
    }

    try {
      await rejectMutation.mutateAsync({
        subscriptionId: rejecting.subscription_id,
        reason,
      })
      toast.success('Request turned down.')
      setRejecting(null)
    } catch (caught) {
      toast.error(
        caught instanceof ApiError
          ? caught.message
          : 'Could not reject this request.',
      )
    }
  }

  const columns: Array<Column<CashRequest>> = [
    {
      key: 'student',
      header: 'Student',
      render: (row) => (
        <div>
          <div className={styles.studentName}>
            {row.student_name ?? 'Unnamed student'}
          </div>
          <div className={styles.muted}>Asked {formatWhen(row.requested_at)}</div>
        </div>
      ),
    },
    {
      key: 'plan',
      header: 'Plan',
      render: (row) => (
        <div>
          <div>{row.plan_name ?? '—'}</div>
          <div className={styles.muted}>
            {row.session_count} session{row.session_count === 1 ? '' : 's'}
          </div>
        </div>
      ),
    },
    {
      key: 'amount',
      header: 'To collect',
      align: 'right',
      render: (row) => (
        <span className="numeric">
          {/* The price AS QUOTED when they tapped Buy — never re-read from the
              plan, which an admin may have edited since. */}
          {row.amount === null ? '—' : formatMoney(row.amount, row.currency)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '210px',
      render: (row) => (
        <div className={styles.rowActions}>
          <Button size="sm" variant="ghost" onClick={() => setRejecting(row)}>
            Reject
          </Button>
          <Button size="sm" variant="primary" onClick={() => setApproving(row)}>
            Mark paid
          </Button>
        </div>
      ),
    },
  ]

  return (
    <PageStack>
      <PageHeader
        title="Cash requests"
        description="Students who have asked for a plan and are paying at the counter."
      />

      {/*
        *** WHY THIS PAGE EXISTS ON A GATEWAY CLIENT ***
        It stays in the navigation either way rather than appearing and
        vanishing with a setting. An admin who finds it needs to know it is not
        broken — it simply does not apply to how this organisation collects
        payment.
      */}
      {isGateway && (
        <Callout tone="info" title="This organisation collects payment online">
          Students pay through the payment gateway when they buy a plan, and
          their subscription activates automatically. There is nothing to
          confirm here. Switching to cash collection is done by the platform
          team.
        </Callout>
      )}

      {isCash && requests.length === 0 && !isLoading && (
        <Callout tone="info" title="Nobody is waiting">
          When a student asks for a plan in the app, they appear here so you can
          confirm their payment.
        </Callout>
      )}

      {isCash && (
        <Card>
          <Table
            columns={columns}
            rows={requests}
            rowKey={(row) => row.subscription_id}
            loading={isLoading}
            error={error}
            onRetry={() => void refetch()}
            emptyMessage="No requests waiting."
          />
        </Card>
      )}

      {/* ------------------------------------------------------- approve */}
      <Modal
        open={approving !== null}
        onClose={() => setApproving(null)}
        title="Confirm payment"
      >
        <form onSubmit={handleApprove}>
          <div className={styles.dialogBody}>
            {/*
              The figure, restated at the moment of confirming. An admin taking
              cash needs it in front of them, not one row back in a table they
              have already stopped looking at.
            */}
            <div className={styles.amountCallout}>
              <span className={styles.amountLabel}>
                Collect from {approving?.student_name ?? 'the student'}
              </span>
              <span className={styles.amountValue}>
                {approving?.amount == null
                  ? '—'
                  : formatMoney(approving.amount, approving.currency)}
              </span>
            </div>

            <p className={styles.muted}>
              {approving?.plan_name ?? 'This plan'} ·{' '}
              {approving?.session_count ?? 0} session
              {approving?.session_count === 1 ? '' : 's'}. Confirming activates
              it straight away, and the student can book immediately.
            </p>

            <TextField
              label="Receipt or note"
              name="collection_note"
              maxLength={500}
              hint="Optional. A receipt number or which counter took it — this is the only record a cash payment has."
            />
          </div>

          <div className={styles.formActions}>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setApproving(null)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={approveMutation.isPending}
            >
              Mark as paid
            </Button>
          </div>
        </form>
      </Modal>

      {/* -------------------------------------------------------- reject */}
      <Modal
        open={rejecting !== null}
        onClose={() => setRejecting(null)}
        title="Turn down this request"
      >
        <form onSubmit={handleReject}>
          <div className={styles.dialogBody}>
            <p className={styles.muted}>
              {rejecting?.student_name ?? 'The student'} will not get{' '}
              {rejecting?.plan_name ?? 'this plan'}, and can ask again later.
            </p>

            <TextField
              label="Reason"
              name="reason"
              required
              minLength={3}
              maxLength={200}
              hint="Kept in the student's history — they will ask why, and this is the answer."
            />
          </div>

          <div className={styles.formActions}>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setRejecting(null)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="danger"
              loading={rejectMutation.isPending}
            >
              Turn down
            </Button>
          </div>
        </form>
      </Modal>
    </PageStack>
  )
}

/** "today at 14:05" reads better than a full timestamp on a live queue. */
function formatWhen(iso: string): string {
  const when = new Date(iso)
  if (Number.isNaN(when.getTime())) return ''

  const time = when.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  })

  const today = new Date()
  const sameDay =
    when.getFullYear() === today.getFullYear() &&
    when.getMonth() === today.getMonth() &&
    when.getDate() === today.getDate()

  if (sameDay) return `today at ${time}`
  return `${when.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} at ${time}`
}
